import React from "react";

import { faExclamationCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { commitLocalUpdate, graphql, type RecordProxy } from "relay-runtime";

import { useSyncedPersistentState } from "~/actionItems/hooks/useSyncedPersistentState";
import Alert from "~/common/Alert";
import type { CreateOrImportProps } from "~/integrations/actionItemIntegration/actionItemLinkingModal";
import { base64Encode } from "~/lib/base64";
import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import { Input } from "~/ui/legacyFellowIndex";

import { LinearAllColumns } from "./selectors/LinearAllColumns";
import { linearStore } from "./store";

import type { LinearCreateExternalTicketPaneMutation as CreateMutation } from "~/__generated__/LinearCreateExternalTicketPaneMutation.graphql";
import type { TeamSelectorQuery } from "~/__generated__/TeamSelectorQuery.graphql";

import styles from "./css/styles.module.css";

export type LinearTeamNode = NonNullable<
	NonNullable<TeamSelectorQuery["response"]["viewer"]["integrations"]["linear"]>["teams"]
>[0];

const createExternalTicket = graphql`
	mutation LinearCreateExternalTicketPaneMutation(
		$blockKey: String!
		$noteId: ID!
		$title: String!
		$destinationInfo: JSON!
	) {
		sendActionItemToIntegration(
			input: {
				blockKey: $blockKey
				noteId: $noteId
				title: $title
				destinationInfo: $destinationInfo
				integrationProvider: LINEAR
			}
		) {
			clientMutationId
		}
	}
`;

function link(blockKey: string, noteId: string, title: string, destinationInfo: { [key: string]: string | undefined }) {
	return new Promise<boolean>(resolve => {
		commitLocalUpdate(environment, store => {
			const relayId = base64Encode(`LinearTeamNode:${destinationInfo.team_id}`);
			const teamNode = store.get(relayId) as RecordProxy<LinearTeamNode>;

			commitMutationPromise<CreateMutation>(environment, {
				mutation: createExternalTicket,
				variables: {
					blockKey,
					noteId,
					title,
					destinationInfo: {
						...destinationInfo,
						// eslint-disable-next-line camelcase
						team_name: teamNode.getValue("name"),
					},
				},
				onCompleted: () => {
					resolve(true);
				},
				onError: () => {
					resolve(false);
				},
			});
		});
	});
}

export function LinearCreateExternalTicketPane({
	setCanSubmit,
	registerSubmit,
	initialText,
	blockKey,
	noteId,
}: CreateOrImportProps) {
	const [teamId, setTeamId] = useSyncedPersistentState<string | undefined>(linearStore, "teamId", undefined);
	const [projectId, setProjectId] = useSyncedPersistentState<string | undefined>(linearStore, "projectId", undefined);
	const [cycleId, setCycleId] = useSyncedPersistentState<string | undefined>(linearStore, "cycleId", undefined);
	const [stateId, setStateId] = useSyncedPersistentState<string | undefined>(linearStore, "stateId", undefined);
	const [uncheckedStateId, setUncheckedStateId] = useSyncedPersistentState<string | undefined>(
		linearStore,
		"uncheckedStateId",
		undefined,
	);
	const [doneStateId, setDoneStateId] = useSyncedPersistentState<string | undefined>(
		linearStore,
		"doneStateId",
		undefined,
	);
	const [title, setTitle] = React.useState<string | undefined>(initialText ? initialText : undefined);
	const [displayListsError, setDisplayListsError] = React.useState(false);

	const onTitleChange = React.useCallback((e: React.FormEvent<HTMLInputElement>) => {
		// @ts-ignore
		setTitle(e.target.value);
	}, []);

	const submit = React.useCallback(async () => {
		if (!title || !teamId) {
			throw new Error("Submission validation step failed");
		}

		/* eslint-disable camelcase */
		const destinationInfo = {
			team_id: teamId,
			project_id: projectId,
			cycle_id: cycleId,
			status_id: stateId,
			unchecked_status_id: uncheckedStateId,
			done_status_id: doneStateId,
		};

		/* eslint-enable camelcase */

		return link(blockKey, noteId, title, destinationInfo);
	}, [blockKey, cycleId, doneStateId, noteId, projectId, stateId, teamId, title, uncheckedStateId]);

	React.useEffect(() => {
		registerSubmit(submit);
	}, [registerSubmit, submit]);

	React.useEffect(() => {
		const mainFieldsFilled = Boolean(teamId && title);
		const extraFieldsFilled =
			Boolean(uncheckedStateId && doneStateId && uncheckedStateId != doneStateId) ||
			Boolean(!uncheckedStateId && !uncheckedStateId);

		setCanSubmit(mainFieldsFilled && extraFieldsFilled);
		setDisplayListsError(Boolean(uncheckedStateId && doneStateId && uncheckedStateId == doneStateId));
	}, [doneStateId, setCanSubmit, teamId, title, uncheckedStateId]);

	return (
		<div>
			{displayListsError && (
				<div className={styles.name}>
					<Alert error rounded icon={faExclamationCircle}>
						You cannot specify the same list for unchecked and done
					</Alert>
				</div>
			)}

			<LinearAllColumns
				teamId={teamId}
				setTeamId={setTeamId}
				projectId={projectId}
				setProjectId={setProjectId}
				cycleId={cycleId}
				setCycleId={setCycleId}
				stateId={stateId}
				setStateId={setStateId}
				uncheckedStateId={uncheckedStateId}
				setUncheckedStateId={setUncheckedStateId}
				doneStateId={doneStateId}
				setDoneStateId={setDoneStateId}
			/>

			<div className={styles.name}>
				<div className={styles.label}>Issue name</div>
				<Input
					name="linear_issue_name"
					className={styles.titleInput}
					type="text"
					placeholder="Set a title for this issue"
					value={title}
					onChange={onTitleChange}
				/>
			</div>

			<div className={styles.help}>
				<div>The issue name, due date, and assignee will automatically sync with Linear.</div>
			</div>
		</div>
	);
}
